/**
 * Shared knowledge: teammate fire solutions near a target, and reverse-
 * simulating an incoming shot to guess where it came from.
 */

import type { PlayerState, PlanetState, GameState, Vec3, GhostMarker, IncomingFireIntel } from '../../types';
import { vecLength, vecSub, vec3 } from '../../math';
import { PHYS_DT, OUT_OF_BOUNDS } from '../../constants';
import { rk4Step, checkCollision } from '../../core/physics';
import { isWasmReady, wasmSimulateTrajectoryHit } from '../wasmPhysics';
import { DEFAULT_BOT_TUNING } from './tuning';

// =============================================================================
// TEAM COORDINATION
// =============================================================================

/**
 * Get fire parameter seeds from teammates who targeted nearby positions.
 * Used to "dial in" on targets that a teammate already found a solution for.
 */
export function getTeammateFireSeeds(
  player: PlayerState,
  gameState: GameState,
  targetPos: Vec3,
  maxDistance: number = DEFAULT_BOT_TUNING.intel.teammateSeedDistance,
): Array<{ elevation: number; power: number }> {
  const seeds: Array<{ elevation: number; power: number }> = [];

  for (const teammate of gameState.players) {
    if (!teammate.alive) continue;
    if (teammate.id === player.id) continue;
    if (teammate.teamIndex !== player.teamIndex) continue;
    if (!teammate.botLastFireParams || !teammate.botLastFireTargetPos) continue;

    const dist = vecLength(vecSub(targetPos, teammate.botLastFireTargetPos));
    if (dist > maxDistance) continue;

    // Generate 9 seeds: 3 elevation offsets × 3 power offsets
    const { elevation, power } = teammate.botLastFireParams;
    for (const elOff of [-0.1, 0, 0.1]) {
      for (const powOff of [-5, 0, 5]) {
        seeds.push({
          elevation: Math.max(0.1, Math.min(1.3, elevation + elOff)),
          power: Math.max(5, Math.min(100, power + powOff)),
        });
      }
    }
  }

  return seeds;
}

// =============================================================================
// BACKTRACK FIRE SOURCE
// =============================================================================

/**
 * Reverse-simulate a projectile's trajectory to estimate which planet it was
 * fired from. Used to generate synthetic ghost markers for bots hit by fire.
 * Returns a GhostMarker at the estimated source, or null if trajectory escapes OOB.
 */
export function backtrackFireSource(
  intel: IncomingFireIntel,
  planets: PlanetState[]
): GhostMarker | null {
  // Negate velocity to simulate backward
  const negVel = vec3(-intel.impactVel.x, -intel.impactVel.y, -intel.impactVel.z);
  const maxSteps = 3600; // 30 seconds backward

  // WASM path: lean simulation (no trajectory points needed)
  if (isWasmReady()) {
    const hit = wasmSimulateTrajectoryHit(
      intel.impactPos, negVel, maxSteps, OUT_OF_BOUNDS, PHYS_DT
    );
    if (hit && hit.planetIndex >= 0) {
      return {
        playerId: intel.shooterId,
        position: hit.point,
        turnSeen: intel.impactTurn,
      };
    }
    return null;
  }

  // JS fallback
  let pos = { ...intel.impactPos };
  let vel = { ...negVel };

  for (let i = 0; i < maxSteps; i++) {
    const result = rk4Step(pos, vel, PHYS_DT, planets);
    pos = result.pos;
    vel = result.vel;

    const collision = checkCollision(pos, planets);
    if (collision) {
      return {
        playerId: intel.shooterId,
        position: collision.point,
        turnSeen: intel.impactTurn,
      };
    }

    if (vecLength(pos) > OUT_OF_BOUNDS) return null;
  }

  return null;
}
